import { useState } from 'react'
import { apiFetch } from '../lib/api'

const SOURCES = [
  { value: 'instagram',  label: 'Instagram' },
  { value: 'twitter',    label: 'Twitter / X' },
  { value: 'friend',     label: 'Friend / Word of mouth' },
  { value: 'google',     label: 'Google Search' },
  { value: 'appstore',   label: 'App Store / Play Store' },
  { value: 'other',      label: 'Other' },
]

export default function FeedbackModal({ onClose }) {
  const [source, setSource]   = useState('')
  const [rating, setRating]   = useState(0)
  const [hover, setHover]     = useState(0)
  const [message, setMessage] = useState('')
  const [loading, setLoading] = useState(false)
  const [done, setDone]       = useState(false)
  const [error, setError]     = useState('')

  async function handleSubmit(e) {
    e.preventDefault()
    if (!source && !rating && !message.trim()) {
      setError('Please fill in at least one field.')
      return
    }
    setError('')
    setLoading(true)
    try {
      const res = await apiFetch('/feedback', {
        method: 'POST',
        body: JSON.stringify({ source: source || undefined, rating: rating || undefined, message: message.trim() || undefined }),
      })
      if (res.ok) {
        setDone(true)
      } else {
        const data = await res.json()
        setError(data.message || 'Something went wrong.')
      }
    } catch {
      setError('Could not submit. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden">

        {/* Header */}
        <div className="bg-gradient-to-r from-orange-500 to-amber-500 px-6 py-5 flex items-center justify-between">
          <div>
            <h2 className="title-font text-lg font-bold text-white">Share your feedback</h2>
            <p className="text-orange-100 text-xs mt-0.5">Help us make Co-Eater better for everyone</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 text-white transition">✕</button>
        </div>

        {done ? (
          <div className="px-6 py-10 flex flex-col items-center gap-3 text-center">
            <span className="text-5xl">🎉</span>
            <p className="text-lg font-bold text-gray-900 title-font">Thank you!</p>
            <p className="text-sm text-gray-500">Your feedback helps us grow and improve.</p>
            <button onClick={onClose} className="mt-4 rounded-full bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold px-8 py-2.5 transition">Done</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="px-6 py-5 space-y-5">

            {/* How did you find us */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">How did you find Co-Eater?</label>
              <div className="grid grid-cols-2 gap-2">
                {SOURCES.map(s => (
                  <button
                    type="button"
                    key={s.value}
                    onClick={() => setSource(s.value)}
                    className={`rounded-2xl border-2 px-3 py-2 text-xs font-medium text-left transition ${
                      source === s.value
                        ? 'border-orange-500 bg-orange-50 text-orange-700'
                        : 'border-gray-200 text-gray-600 hover:border-orange-300 hover:bg-orange-50'
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Star rating */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">How would you rate your experience?</label>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map(star => (
                  <button
                    type="button"
                    key={star}
                    onClick={() => setRating(star)}
                    onMouseEnter={() => setHover(star)}
                    onMouseLeave={() => setHover(0)}
                    className="text-3xl leading-none transition-transform hover:scale-110 focus:outline-none"
                  >
                    <span className={(hover || rating) >= star ? 'text-amber-400' : 'text-gray-300'}>★</span>
                  </button>
                ))}
                {rating > 0 && (
                  <span className="ml-2 text-sm text-gray-500 self-center">
                    {['', 'Poor', 'Fair', 'Good', 'Great', 'Excellent'][rating]}
                  </span>
                )}
              </div>
            </div>

            {/* Message */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                What would you like to see improved or added?
              </label>
              <textarea
                rows={3}
                value={message}
                onChange={e => setMessage(e.target.value)}
                placeholder="Tell us what features you'd love, what's missing, or anything else on your mind…"
                className="w-full rounded-2xl border border-orange-200 bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300 resize-none"
                maxLength={1000}
              />
              <p className="text-xs text-gray-400 text-right mt-0.5">{message.length}/1000</p>
            </div>

            {error && <p className="text-xs text-red-500 font-medium">{error}</p>}

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-2xl bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white font-semibold py-3 text-sm transition disabled:opacity-60"
            >
              {loading ? 'Submitting…' : 'Submit Feedback'}
            </button>
          </form>
        )}
      </div>
    </div>
  )
}
